import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Layout, Card, Tabs, Button, Empty, Result, Menu, Avatar, Input, Drawer, message, Divider, List, Dropdown, Tag, Space, Modal, Alert } from 'antd';
import { QuestionCircleOutlined, EditOutlined, EllipsisOutlined, BookOutlined, UserAddOutlined, DeleteOutlined, StarOutlined, MessageOutlined, FileTextOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { supabase } from './supabaseClient';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { intervalToDuration, formatDuration } from 'date-fns';
import { useTranslation } from 'react-i18next';
import AIChatInterface from './AIChatInterface';
import { marked } from 'marked';
import { EyeOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { DownloadOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import TranscriptTab from './details/TranscriptTab';
import SummaryTab from './details/SummaryTab';
import NotesTab from './details/NotesTab';
import './MeetingDetail.css';

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;
const { TabPane } = Tabs;
const { Search } = Input;


// InviteModal 컴포넌트를 MeetingDetail 외부로 분리
const InviteModal = ({ visible, onCancel, meetingId, meetingTitle, onInviteSuccess }) => {
    const [emailInputs, setEmailInputs] = useState(['']); // Start with one empty input
    const [isInviting, setIsInviting] = useState(false);
    const { t } = useTranslation();

    const addEmailInput = () => {
        setEmailInputs([...emailInputs, '']);
    };

    const removeEmailInput = (index) => {
        const newInputs = emailInputs.filter((_, i) => i !== index);
        setEmailInputs(newInputs.length ? newInputs : ['']); // Keep at least one input
    };

    const handleEmailChange = (index, value) => {
        const newInputs = [...emailInputs];
        newInputs[index] = value;
        setEmailInputs(newInputs);
    };

    const handleInvite = async () => {
        const validEmails = emailInputs.filter(email => email.trim());
        if (validEmails.length === 0) {
            message.error(t('Please enter at least one email address'));
            return;
        }

        setIsInviting(true);
        const results = {
            success: [],
            alreadySent: [],
            failed: []
        };

        for (const email of validEmails) {
            try {
                const { data, error } = await supabase.functions.invoke('send-invitation-email', {
                    body: {
                        meetingId,
                        meetingTitle,
                        recipientEmail: email.trim()
                    }
                });

                if (error) {
                    // Check if the error is specifically about already sent invitation
                    if (error.message === 'Invitation already sent to this email') {
                        results.alreadySent.push(email);
                    } else {
                        results.failed.push(email);
                        console.error('Invitation error:', error);
                    }
                } else {
                    results.success.push(email);
                }
            } catch (error) {
                console.error('Unexpected error:', error);
                results.failed.push(email);
            }
        }

        // Show results
        if (results.success.length > 0) {
            message.success(t('Invitations sent successfully to: ') + results.success.join(', '));
        }
        if (results.alreadySent.length > 0) {
            message.warning(t('Invitations were already sent to: ') + results.alreadySent.join(', '));
        }
        if (results.failed.length > 0) {
            message.error(t('Failed to send invitations to: ') + results.failed.join(', '));
        }

        // Only close modal if all emails were either successful or already sent
        if (results.success.length > 0 || results.alreadySent.length > 0) {
            if (results.failed.length === 0) {  // Only if there were no failures
                onInviteSuccess?.();
                setEmailInputs(['']);
                onCancel();
            }
        }
        setIsInviting(false);
    };

    return (
        <Modal
            title={t("Invite Members")}
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    {t("Cancel")}
                </Button>,
                <Button
                    key="invite"
                    type="primary"
                    loading={isInviting}
                    onClick={handleInvite}
                >
                    {t("Send Invitations")}
                </Button>
            ]}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                    <Text>{t("Enter email addresses to invite:")}</Text>
                    <div style={{ marginTop: '8px' }}>
                        {emailInputs.map((email, index) => (
                            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <Input
                                    value={email}
                                    onChange={(e) => handleEmailChange(index, e.target.value)}
                                    placeholder="email@example.com"
                                    style={{ flex: 1 }}
                                />
                                {emailInputs.length > 1 && (
                                    <Button
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeEmailInput(index)}
                                        danger
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                    <Button
                        type="dashed"
                        onClick={addEmailInput}
                        style={{ width: '100%', marginTop: '8px' }}
                        icon={<PlusOutlined />}
                    >
                        {t("Add Another Email")}
                    </Button>
                </div>
                <Alert
                    message={t("Note")}
                    description={t("The invited users will receive access to view this meeting's content.")}
                    type="info"
                    showIcon
                />
            </Space>
        </Modal>
    );
};


const NoAccessPage = () => {
    const { t } = useTranslation();

    return (
        <div style={{
            maxWidth: '400px',
            margin: '100px auto',
            padding: '24px',
            textAlign: 'center'
        }}>
            <Result
                status="403"
                title={t("No Access")}
                subTitle={
                    <Space direction="vertical">
                        <Text>{t("You don't have permission to access this meeting")}</Text>
                        <Text type="secondary">
                            {t("Please contact the meeting owner to request access")}
                        </Text>
                    </Space>
                }
            />
        </div>
    );
};


const ReviewReminderModal = ({ visible, onClose, onReviewed }) => {
    const { t } = useTranslation();

    return (
        <Modal
            title={t("❤️ Love using EliMeetNote?")}
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="reviewed" onClick={onReviewed}>
                    {t("I've already reviewed")}
                </Button>,
                <Button key="review" type="primary" icon={<StarOutlined />} onClick={() => window.open('https://chromewebstore.google.com/detail/%EB%AC%B4%EB%A3%8C-google-meet-%EB%85%B8%ED%8A%B8%ED%8C%A8%EB%93%9C-%EC%8B%A4%EC%8B%9C%EA%B0%84-%EB%B2%88/jlcdehhmjnofkofdgelhabdfiaodiljp?authuser=0', '_blank')}>
                    {t("Leave a Review & Get Free Tokens!")}
                </Button>,
                <Button key="later" onClick={onClose}>
                    {t("Remind me later")}
                </Button>
            ]}
        >

            <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
                <p><strong>Hi there! I'm the solo developer behind EliMeetNote.</strong></p>

                <p>If you're finding EliMeetNote helpful in your meetings, would you consider taking a moment to leave a review? Your feedback would mean the world to me and help others discover this tool! 🌟</p>

                <p><strong>🎁 As a thank you for your support:</strong></p>
                <ul style={{ paddingLeft: '20px' }}>
                    <li>Get <strong>free tokens</strong> for real-time translation</li>
                    <li>Get <strong>free tokens</strong> for live meeting summarization</li>
                    <li>Priority support for future feature requests</li>
                </ul>

                <p style={{ marginTop: '15px', color: '#1890ff' }}>
                    <strong>Just screenshot your review and send it to elimeetagent@gmail.com to claim your rewards! 🎉</strong>
                </p>
            </div>
        </Modal>
    );
};

// Add ShareSuggestionModal component
const ShareSuggestionModal = ({ visible, onCancel, meetingId, meetingTitle, onShareSuccess }) => {
    const [emailInputs, setEmailInputs] = useState(['']);
    const [isSharing, setIsSharing] = useState(false);
    const { t } = useTranslation();

    const addEmailInput = () => {
        setEmailInputs([...emailInputs, '']);
    };

    const removeEmailInput = (index) => {
        const newInputs = emailInputs.filter((_, i) => i !== index);
        setEmailInputs(newInputs.length ? newInputs : ['']);
    };

    const handleEmailChange = (index, value) => {
        const newInputs = [...emailInputs];
        newInputs[index] = value;
        setEmailInputs(newInputs);
    };

    const handleShare = async () => {
        // Reuse the same invitation logic
        const validEmails = emailInputs.filter(email => email.trim());
        if (validEmails.length === 0) {
            message.error(t('Please enter at least one email address'));
            return;
        }

        setIsSharing(true);
        const results = {
            success: [],
            alreadySent: [],
            failed: []
        };

        for (const email of validEmails) {
            try {
                const { data, error } = await supabase.functions.invoke('send-invitation-email', {
                    body: {
                        meetingId,
                        meetingTitle,
                        recipientEmail: email.trim()
                    }
                });

                if (error) {
                    if (error.message === 'Invitation already sent to this email') {
                        results.alreadySent.push(email);
                    } else {
                        results.failed.push(email);
                    }
                } else {
                    results.success.push(email);
                }
            } catch (error) {
                results.failed.push(email);
            }
        }

        // Show results
        if (results.success.length > 0) {
            message.success(t('Meeting shared successfully with: ') + results.success.join(', '));
        }
        if (results.alreadySent.length > 0) {
            message.warning(t('Meeting was already shared with: ') + results.alreadySent.join(', '));
        }
        if (results.failed.length > 0) {
            message.error(t('Failed to share with: ') + results.failed.join(', '));
        }

        if (results.success.length > 0 || results.alreadySent.length > 0) {
            if (results.failed.length === 0) {
                onShareSuccess?.();
                setEmailInputs(['']);
                onCancel();
            }
        }
        setIsSharing(false);
    };

    return (
        <Modal
            title={
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                        🎉 {t("Meeting Successfully Completed!")}
                    </div>
                    <Text type="secondary">
                        {t("Share the valuable insights with your team")}
                    </Text>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width={600}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    {t("Maybe Later")}
                </Button>,
                <Button
                    key="share"
                    type="primary"
                    loading={isSharing}
                    onClick={handleShare}
                    icon={<UserAddOutlined />}
                >
                    {t("Share with Team")}
                </Button>
            ]}
            centered
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Alert
                    message={
                        <Text strong style={{ fontSize: '16px' }}>
                            {t("Why Share This Meeting?")}
                        </Text>
                    }
                    description={
                        <div style={{ marginTop: '12px' }}>
                            <div style={{
                                padding: '16px',
                                background: '#f8f8f8',
                                borderRadius: '8px',
                                marginBottom: '16px'
                            }}>
                                <Text>{t("Meeting Title")}: <Text strong>{meetingTitle}</Text></Text>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                                    <Space>
                                        <FileTextOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                                        <Text strong>{t("Accurate Transcripts")}</Text>
                                    </Space>
                                    <div style={{ marginTop: '8px' }}>
                                        {t("Never miss important details with complete meeting transcripts")}
                                    </div>
                                </Card>
                                <Card size="small" style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}>
                                    <Space>
                                        <BookOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                                        <Text strong>{t("AI Summaries")}</Text>
                                    </Space>
                                    <div style={{ marginTop: '8px' }}>
                                        {t("Quick catch-up with AI-generated meeting summaries")}
                                    </div>
                                </Card>
                            </div>
                            <Alert
                                message={
                                    <Space>
                                        <InfoCircleOutlined />
                                        <Text strong>{t("Privacy Notice")}</Text>
                                    </Space>
                                }
                                description={
                                    <div style={{ marginTop: '8px' }}>
                                        <Text>{t("For privacy reasons, the following personal interactions will not be shared:")}</Text>
                                        <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                                            <li>{t("Bookmarks you've created during the meeting")}</li>
                                            <li>{t("Personal notes and annotations")}</li>
                                        </ul>
                                        <Text type="secondary" style={{ fontSize: '13px', marginTop: '8px', display: 'block' }}>
                                            {t("Sharing features for these personal interactions may be added in future updates.")}
                                        </Text>
                                    </div>
                                }
                                type="warning"
                                style={{ marginBottom: '16px' }}
                            />
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px' }}
                />

                <div style={{ background: '#fafafa', padding: '16px', borderRadius: '8px' }}>
                    <Text strong>{t("Share with Team Members")}</Text>
                    <div style={{ marginTop: '12px' }}>
                        {emailInputs.map((email, index) => (
                            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <Input
                                    value={email}
                                    onChange={(e) => handleEmailChange(index, e.target.value)}
                                    placeholder={t("Enter team member's email")}
                                    style={{ flex: 1 }}
                                    prefix={<UserAddOutlined style={{ color: '#bfbfbf' }} />}
                                />
                                {emailInputs.length > 1 && (
                                    <Button
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeEmailInput(index)}
                                        danger
                                    />
                                )}
                            </div>
                        ))}
                        <Button
                            type="dashed"
                            onClick={addEmailInput}
                            style={{ width: '100%', marginTop: '8px' }}
                            icon={<PlusOutlined />}
                        >
                            {t("Add Another Team Member")}
                        </Button>
                    </div>
                </div>
            </Space>
        </Modal>
    );
};

// Add EndMeetingModal component
const EndMeetingModal = ({ visible, onCancel, onConfirm, isUpdating }) => {
    const { t } = useTranslation();

    return (
        <Modal
            title={t("End Meeting")}
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    {t("No, keep it ongoing")}
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={isUpdating}
                    onClick={onConfirm}
                >
                    {t("Yes, end meeting")}
                </Button>
            ]}
        >
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <QuestionCircleOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
                <Title level={4}>{t("Has this meeting ended?")}</Title>
                <Text type="secondary">
                    {t("If the meeting has concluded, we'll mark it as finished and calculate the final duration.")}
                </Text>
            </div>
        </Modal>
    );
};

const MeetingDetail = ({ user }) => {
    const [meetingTitle, setMeetingTitle] = useState('');
    const [meetingData, setMeetingData] = useState(null);
    const [diarizedTranscriptions, setDiarizedTranscriptions] = useState([]);
    const [temporalTranscriptions, setTemporalTranscriptions] = useState([]);
    const [showAIChat, setShowAIChat] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [selectedTranscript, setSelectedTranscript] = useState(null);
    const [summaries, setSummaries] = useState([]);
    const [aiAdvices, setAiAdvices] = useState([]);
    const [selectedContext, setSelectedContext] = useState(null);
    const [transcriptTags, setTranscriptTags] = useState({});
    const [meetingNotes, setMeetingNotes] = useState('');
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    const [hasAccess, setHasAccess] = useState(true);
    const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
    const [showReviewReminder, setShowReviewReminder] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showShareSuggestion, setShowShareSuggestion] = useState(false);
    const [showEndMeetingModal, setShowEndMeetingModal] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    const { id } = useParams();
    const { t } = useTranslation();

    // Move handleSearch to top level with other hooks
    const handleSearch = useCallback(
        debounce((query) => {
            if (!query.trim()) {
                setSearchResults([]);
                return;
            }

            setIsSearching(true);
            const results = [];

            // Search in transcriptions
            if (diarizedTranscriptions) {
                const transcriptMatches = diarizedTranscriptions.filter(item =>
                    item.text.toLowerCase().includes(query.toLowerCase())
                );
                results.push(...transcriptMatches.map(item => ({
                    type: 'transcript',
                    content: item.text,
                    speaker: item.speaker_id,
                    time: item.start_time,
                    id: item.uuid
                })));
            }

            // Search in summaries
            if (summaries) {
                const summaryMatches = summaries.filter(item =>
                    item.summary_text.toLowerCase().includes(query.toLowerCase())
                );
                results.push(...summaryMatches.map(item => ({
                    type: 'summary',
                    content: item.summary_text,
                    time: item.start_time,
                    id: item.id
                })));
            }

            // Search in notes
            if (meetingNotes) {
                const noteLines = meetingNotes.split('\n');
                const noteMatches = noteLines.filter(line =>
                    line.toLowerCase().includes(query.toLowerCase())
                );
                results.push(...noteMatches.map((line, index) => ({
                    type: 'note',
                    content: line,
                    id: `note-${index}`
                })));
            }

            setSearchResults(results);
            setIsSearching(false);
        }, 300),
        [diarizedTranscriptions, summaries, meetingNotes]
    );

    // Move Hooks to the top level
    const memoizedTranscriptions = useMemo(() => {
        if (!diarizedTranscriptions) return [];
        return diarizedTranscriptions.map(item => ({
            ...item,
            id: item.uuid
        }));
    }, [diarizedTranscriptions]);

    const memoizedFilteredTranscriptions = useMemo(() => {
        if (!memoizedTranscriptions || !transcriptTags) return {
            important: [],
            action_item: [],
            unclear: [],
            note: []
        };

        return {
            important: memoizedTranscriptions.filter(item =>
                transcriptTags[item.id]?.some(tag => tag.tag_type === 'important')
            ),
            action_item: memoizedTranscriptions.filter(item =>
                transcriptTags[item.id]?.some(tag => tag.tag_type === 'action_item')
            ),
            unclear: memoizedTranscriptions.filter(item =>
                transcriptTags[item.id]?.some(tag => tag.tag_type === 'unclear')
            ),
            note: memoizedTranscriptions.filter(item =>
                transcriptTags[item.id]?.some(tag => tag.tag_type === 'note')
            )
        };
    }, [memoizedTranscriptions, transcriptTags]);

    const handleAskAIMemoized = useCallback((transcript) => {
        setSelectedTranscript(transcript);
        setShowAIChat(true);
    }, []);

    const fetchMeetingDetails = async () => {
        try {
            const [
                meetingResponse,
                transcriptionResponse,
                summaryResponse,
                adviceResponse,
                tagResponse,
                notesResponse
            ] = await Promise.all([
                supabase.from('meetings').select('*').eq('id', id).single(),
                supabase.from('transcriptions').select('diarized_transcription, temporal_transcription').eq('meeting_id', id).single(),
                supabase.from('meeting_summaries').select('*').eq('meeting_id', id).order('start_time', { ascending: true }),
                supabase.from('meeting_advices').select('*').eq('meeting_id', id).order('created_at', { ascending: false }),
                supabase.from('transcript_tags').select('*').eq('meeting_id', id),
                supabase.from('meeting_notes').select('content').eq('meeting_id', id).single()
            ]);

            if (meetingResponse.error) {
                if (meetingResponse.error.code === 'PGRST116' || meetingResponse.error.message.includes('permission denied')) {
                    setHasAccess(false);
                    return;
                }
                throw meetingResponse.error;
            }

            setMeetingTitle(meetingResponse.data.title);
            setMeetingData(meetingResponse.data);

            if (transcriptionResponse.data) {
                if (transcriptionResponse.data.diarized_transcription) {
                    setDiarizedTranscriptions(transcriptionResponse.data.diarized_transcription);
                }
                if (transcriptionResponse.data.temporal_transcription) {
                    setTemporalTranscriptions(transcriptionResponse.data.temporal_transcription);
                }
            }

            if (summaryResponse.data) {
                setSummaries(summaryResponse.data);
            }

            if (adviceResponse.data) {
                setAiAdvices(adviceResponse.data);
            }

            if (tagResponse.data) {
                const tagsByMessage = tagResponse.data.reduce((acc, tag) => {
                    if (!acc[tag.message_id]) {
                        acc[tag.message_id] = [];
                    }
                    acc[tag.message_id].push(tag);
                    return acc;
                }, {});
                setTranscriptTags(tagsByMessage);
            }

            if (notesResponse.data) {
                setMeetingNotes(notesResponse.data.content || '');
            }

            if (meetingResponse.data &&
                !meetingResponse.data.is_visited &&
                meetingResponse.data.status === 'finished') {
                setShowShareSuggestion(true);
            }

        } catch (error) {
            console.error(t("Error fetching meeting details:"), error);
            message.error(t("Failed to load meeting details."));
        }
    };



    useEffect(() => {
        fetchMeetingDetails();
    }, [id]);

    useEffect(() => {
        const checkReviewReminder = () => {
            console.log('checkReviewReminder');
            const hasReviewed = localStorage.getItem('hasReviewed') === 'true';
            console.log('hasReviewed', hasReviewed);
            if (hasReviewed) return;

            const visitCount = parseInt(localStorage.getItem('visitCount') || '0');
            localStorage.setItem('visitCount', (visitCount + 1).toString());

            if ((visitCount + 1) % 10 === 4) {
                setShowReviewReminder(true);
            }
        };

        checkReviewReminder();
    }, []);

    // Add this effect to check meeting status
    useEffect(() => {
        if (meetingData && meetingData.status === 'ongoing') {
            setShowEndMeetingModal(true);
        }
    }, [meetingData]);

    // Early return after Hooks
    if (!hasAccess) {
        return <NoAccessPage />;
    }

    if (!meetingData) {
        return <div>{t("Loading...")}</div>;
    }

    const onDeleteTag = async (messageId, tagId) => {
        try {
            const { error } = await supabase
                .from('transcript_tags')
                .delete()
                .eq('id', tagId);

            if (error) throw error;

            // Update local state to remove the deleted tag
            setTranscriptTags(prevTags => ({
                ...prevTags,
                [messageId]: prevTags[messageId].filter(tag => tag.id !== tagId)
            }));

            message.success(t('Tag removed successfully'));
        } catch (error) {
            console.error('Error removing tag:', error);
            message.error(t('Failed to remove tag'));
        }
    };

    const onAddTag = async (messageId, tagType, noteContent = null) => {
        try {
            const { data, error } = await supabase
                .from('transcript_tags')
                .insert([{
                    meeting_id: id,
                    message_id: messageId,
                    user_id: user.id,
                    tag_type: tagType,
                    note_content: noteContent,
                }])
                .select(); // add .select() to return the inserted data

            if (error) throw error;

            if (!data || data.length === 0) {
                throw new Error('No data returned from insert');
            }

            // Update local state to reflect the new tag
            setTranscriptTags(prevTags => ({
                ...prevTags,
                [messageId]: [
                    ...(prevTags[messageId] || []),
                    {
                        id: data[0].id,
                        message_id: messageId,
                        tag_type: tagType,
                        note_content: noteContent
                    }
                ]
            }));

            message.success(t('Tag added successfully'));
        } catch (error) {
            console.error('Error adding tag:', error);
            message.error(t('Failed to add tag'));
        }
    };
    // Add function inside MeetingDetail component
    const exportAllToExcel = () => {
        const wb = XLSX.utils.book_new();

        // Transcript sheet
        const transcriptData = diarizedTranscriptions.map(item => ({
            Time: formatTime(item.start_time),
            Speaker: item.speaker_id,
            Content: item.text
        }));
        const transcriptSheet = XLSX.utils.json_to_sheet(transcriptData);
        XLSX.utils.book_append_sheet(wb, transcriptSheet, 'Transcript');

        // Summary sheet
        const summaryData = summaries.map(item => ({
            StartTime: formatTime(item.start_time),
            EndTime: formatTime(item.end_time),
            Summary: item.summary_text
        }));
        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

        // Notes sheet
        const notesData = [{
            Content: meetingNotes,
            CreatedAt: formatDate(new Date())
        }];
        const notesSheet = XLSX.utils.json_to_sheet(notesData);
        XLSX.utils.book_append_sheet(wb, notesSheet, 'Notes');

        // Save file
        XLSX.writeFile(wb, `${meetingData.title}_meeting_notes.xlsx`);
    };

    const formatTime = (seconds) => {
        // ISO 문자열을 Date 객체로 변환 후 유닉스 타임스탬프(밀리초)를 초로 변환
        const unixSeconds = Math.floor(new Date(seconds).getTime() / 1000);

        const hours = Math.floor(unixSeconds / 3600);
        const minutes = Math.floor((unixSeconds % 3600) / 60);
        const secs = Math.floor(unixSeconds % 60);

        let result = '';
        if (hours > 0) result += `${hours}h `;
        if (minutes > 0 || hours > 0) result += `${minutes}m `;
        result += `${secs}s`;

        return result.trim();
    };

    const handleEditTitle = () => {
        setNewTitle(meetingData.title);
        setIsEditingTitle(true);
    };

    const handleTitleChange = (e) => {
        setNewTitle(e.target.value);
    };
    const handleDeleteMeeting = async () => {
        const confirmed = window.confirm(t('Are you sure you want to delete this meeting? This action cannot be undone.'));

        if (confirmed) {
            const { error } = await supabase
                .from('meetings')
                .delete()
                .eq('id', id);

            if (error) {
                console.error(t('Error deleting meeting:'), error);
                message.error(t('Failed to delete meeting'));
            } else {
                message.success(t('Meeting deleted successfully'));
                // Redirect to meetings list
                window.location.href = '/';
            }
        }
    };

    const handleTitleSubmit = async () => {
        if (newTitle.trim() === '') {
            message.error(t('Title cannot be empty'));
            return;
        }

        const { error } = await supabase
            .from('meetings')
            .update({ title: newTitle })
            .eq('id', id);

        if (error) {
            console.error(t('Error updating title:'), error);
            message.error(t('Failed to update title'));
        } else {
            setMeetingTitle(newTitle); // meeting 전체 대신 title만 업데이트
            message.success(t('Title updated successfully'));
        }

        setIsEditingTitle(false);
    };


    // AI 조언 렌더링 함수 추가
    // renderAdviceItem 함수 수정
    const renderAdviceItem = (item) => {
        // 컨텍스트 텍스트를 줄바꿈으로 분리하고 각 발화자별로 구조화
        const formatContextText = (text) => {
            if (!text) return [];
            return text.split('\n').map((line, index) => {
                const [speaker, ...message] = line.split(':');
                return {
                    id: index,
                    speaker: speaker?.trim(),
                    message: message.join(':').trim()
                };
            }).filter(item => item.speaker && item.message);
        };

        return (
            <Card
                style={{
                    marginBottom: '16px',
                    borderRadius: '8px',
                    backgroundColor: '#f8f8f8'
                }}
            >
                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">
                        {formatDate(item.created_at)}
                    </Text>
                    <Space>
                        <Button
                            type="link"
                            onClick={() => setSelectedContext(item)}
                            icon={<EyeOutlined />}
                        >
                            {t("View Context")}
                        </Button>
                        <Tag color={item.advice_type === 'realtime' ? 'blue' : 'green'}>
                            {item.advice_type === 'realtime' ? t('AI Advice') : t('User Requested')}
                        </Tag>
                    </Space>
                </div>

                {item.user_prompt && (
                    <div style={{ marginBottom: '12px', backgroundColor: '#e6f7ff', padding: '8px', borderRadius: '4px' }}>
                        <Text strong>{t("User Question")}: </Text>
                        <Text>{item.user_prompt}</Text>
                    </div>
                )}

                <div
                    style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '4px' }}
                    dangerouslySetInnerHTML={{
                        __html: marked.parse(item.advice_text)
                    }}
                    className="markdown-content"
                />
            </Card>
        );
    };

    // 컨텍스트 드로어 컴포넌트 추가
    const ContextDrawer = ({ context, onClose }) => {
        if (!context) return null;

        const contextMessages = context.context_text.split('\n').map((line, index) => {
            const [speaker, ...message] = line.split(':');
            return {
                id: index,
                speaker: speaker?.trim(),
                message: message.join(':').trim()
            };
        }).filter(item => item.speaker && item.message);

        return (
            <Drawer
                title={t("Conversation Context")}
                placement="right"
                onClose={onClose}
                open={true}
                width={500}
                extra={
                    <Space>
                        <Text type="secondary">
                            {formatDate(context.created_at)}
                        </Text>
                    </Space>
                }
            >
                <List
                    dataSource={contextMessages}
                    renderItem={item => (
                        <List.Item style={{
                            padding: '12px',
                            borderRadius: '8px',
                            backgroundColor: '#f8f8f8',
                            marginBottom: '8px'
                        }}>
                            <List.Item.Meta
                                avatar={
                                    <Avatar style={{ backgroundColor: stringToColor(item.speaker) }}>
                                        {item.speaker[0]}
                                    </Avatar>
                                }
                                title={<Text strong>{item.speaker}</Text>}
                                description={item.message}
                            />
                        </List.Item>
                    )}
                />

                <Divider />

                <div>
                    <Text strong>{t("AI Response")}:</Text>
                    <div
                        className="markdown-content"
                        style={{
                            marginTop: '12px',
                            padding: '16px',
                            backgroundColor: '#f0f8ff',
                            borderRadius: '8px'
                        }}
                        dangerouslySetInnerHTML={{
                            __html: marked.parse(context.advice_text)
                        }}
                    />
                </div>
            </Drawer>
        );
    };

    // 유틸리티 함수: 문자열을 컬러 코드로 변환
    const stringToColor = (str) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const colors = [
            '#1890ff', '#52c41a', '#faad14', '#f5222d',
            '#722ed1', '#13c2c2', '#eb2f96', '#fadb14'
        ];
        return colors[Math.abs(hash) % colors.length];
    };

    const formatDate = (dateString) => {
        const date = toDate(dateString);
        return formatInTimeZone(date, Intl.DateTimeFormat().resolvedOptions().timeZone, "yyyy-MM-dd HH:mm:ss (zzz)");
    };


    const calculateDuration = (startDate, endDate) => {
        if (!endDate) return t("In progress");
        const start = new Date(startDate);
        const end = new Date(endDate);
        const duration = intervalToDuration({ start, end });
        const hours = duration.hours || 0;
        const minutes = duration.minutes || 0;
        const seconds = duration.seconds || 0;
        return `${hours}${t("h")} ${minutes}${t("m")} ${seconds}${t("s")}`;
    };

    const handleReviewModalClose = () => {
        setShowReviewReminder(false);
    };

    const handleReviewed = () => {
        localStorage.setItem('hasReviewed', 'true');
        setShowReviewReminder(false);
        message.success(t('Thank you for your review!'));
    };

    // Add highlightText helper function
    const highlightText = (text, query) => {
        if (!query.trim()) return text;

        const parts = text.split(new RegExp(`(${query})`, 'gi'));
        return (
            <span>
                {parts.map((part, index) =>
                    part.toLowerCase() === query.toLowerCase() ? (
                        <span key={index} style={{ backgroundColor: '#ffd54f', padding: '0 2px' }}>
                            {part}
                        </span>
                    ) : (
                        part
                    )
                )}
            </span>
        );
    };

    // Update renderSearchResult to include highlighting
    const renderSearchResult = (result) => {
        const getIcon = (type) => {
            switch (type) {
                case 'transcript':
                    return <MessageOutlined />;
                case 'summary':
                    return <FileTextOutlined />;
                case 'note':
                    return <EditOutlined />;
                default:
                    return null;
            }
        };

        return (
            <List.Item>
                <List.Item.Meta
                    avatar={<Avatar icon={getIcon(result.type)} />}
                    title={
                        <Space>
                            <Tag color={result.type === 'transcript' ? 'blue' : result.type === 'summary' ? 'green' : 'purple'}>
                                {t(result.type.charAt(0).toUpperCase() + result.type.slice(1))}
                            </Tag>
                            {result.speaker && <Tag>{result.speaker}</Tag>}
                            {result.time && <Text type="secondary">{formatTime(result.time)}</Text>}
                        </Space>
                    }
                    description={highlightText(result.content, searchQuery)}
                />
            </List.Item>
        );
    };

    // Add this function to handle marking the meeting as visited
    const markMeetingAsVisited = async () => {
        const { error } = await supabase
            .from('meetings')
            .update({ is_visited: true })
            .eq('id', id);

        if (error) {
            console.error('Error updating meeting visited status:', error);
        }
    };

    // Add function to handle meeting end
    const handleEndMeeting = async () => {
        setIsUpdatingStatus(true);
        try {
            const endedAt = new Date().toISOString();
            const { error } = await supabase
                .from('meetings')
                .update({
                    status: 'finished',
                    ended_at: endedAt
                })
                .eq('id', id);

            if (error) throw error;

            // Update local state
            setMeetingData(prev => ({
                ...prev,
                status: 'finished',
                ended_at: endedAt
            }));

            message.success(t('Meeting marked as finished'));
            setShowEndMeetingModal(false);
        } catch (error) {
            console.error('Error updating meeting status:', error);
            message.error(t('Failed to update meeting status'));
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    return (
        <Layout style={{ overflow: 'hidden' }}>
            <Content style={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                <Card style={{ marginBottom: '24px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            {isEditingTitle ? (
                                <Input
                                    value={newTitle}
                                    onChange={handleTitleChange}
                                    onPressEnter={handleTitleSubmit}
                                    onBlur={handleTitleSubmit}
                                    autoFocus
                                />
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Title level={2} style={{ marginBottom: 0, marginRight: '10px' }}>{meetingTitle}</Title>
                                    <Button icon={<EditOutlined />} onClick={handleEditTitle} />
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {meetingData.user_id === (user.id) && (
                                <>
                                    <Button
                                        icon={<UserAddOutlined />}
                                        onClick={() => setIsInviteModalVisible(true)}
                                    >
                                        {t("Invite")}
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<DownloadOutlined />}
                                        onClick={exportAllToExcel}
                                    >
                                        {t("Export")}
                                    </Button>
                                    <Button
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={handleDeleteMeeting}
                                    >
                                        {t("Delete")}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                    <Divider />
                    {/* <Text strong>{t("Creator")}: </Text><Text>{meetingData.user_name}</Text>
                    <br /> */}
                    <Text strong>{t("Created Date")}: </Text><Text>{formatDate(meetingData.created_at)}</Text>
                    <br />
                    <Text strong>{t("End Date")}: </Text><Text>{meetingData.ended_at ? formatDate(meetingData.ended_at) : t("In progress")}</Text>
                    <br />
                    <Text strong>{t("Duration")}: </Text><Text>{calculateDuration(meetingData.created_at, meetingData.ended_at)}</Text>
                    <br />
                    <Text strong>{t("Recording Method")}: </Text>
                    <Text>
                        {meetingData.record_method === 'direct_record' && t("Direct Recording")}
                        {meetingData.record_method === 'zoom' && 'Zoom'}
                        {meetingData.record_method === 'google_meet' && 'Google Meet'}
                    </Text>
                </Card>

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0
                }}>
                    <Title level={3}>{t("Meeting Content")}</Title>
                    <div style={{ width: '300px' }}>
                        <Search
                            placeholder={t("Search in meeting content...")}
                            allowClear
                            loading={isSearching}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                handleSearch(e.target.value);
                            }}
                        />
                    </div>
                </div>

                {searchQuery && (
                    <Card style={{ marginBottom: '16px', flexShrink: 0 }}>
                        <div style={{ marginBottom: '16px' }}>
                            <Text strong>{t("Search Results")}</Text>
                            {searchResults.length > 0 && (
                                <Text type="secondary" style={{ marginLeft: '8px' }}>
                                    ({searchResults.length} {t("results found")})
                                </Text>
                            )}
                        </div>
                        {searchResults.length > 0 ? (
                            <List
                                itemLayout="vertical"
                                dataSource={searchResults}
                                renderItem={renderSearchResult}
                            />
                        ) : (
                            <Empty description={t("No results found")} />
                        )}
                    </Card>
                )}

                <div
                    style={{
                        flex: 1,
                        minHeight: 0, // Important to allow children to shrink below the content size
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >

                    <Tabs
                        defaultActiveKey="1"
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            background: '#fff',
                            padding: '16px'
                        }}
                    >
                        <TabPane tab={t("Transcript & Translation")} key="1">
                            <TranscriptTab
                                memoizedTranscriptions={memoizedTranscriptions}
                                memoizedFilteredTranscriptions={memoizedFilteredTranscriptions}
                                handleAskAIMemoized={handleAskAIMemoized}
                                transcriptTags={transcriptTags}
                                onAddTag={onAddTag}
                                onDeleteTag={onDeleteTag}
                            />
                        </TabPane>
                        <TabPane
                            tab={t("Live Summary")}
                            key="2"
                            style={{
                                height: '100%',
                                overflow: 'auto'
                            }}
                        >
                            <SummaryTab summaries={summaries} />
                        </TabPane>
                        <TabPane
                            tab={t("Meeting Notes")}
                            key="3"
                            style={{
                                height: '100%',
                                overflow: 'auto',
                                padding: '16px'
                            }}
                        >
                            <NotesTab
                                meetingId={id}
                                userId={user.id}
                                initialNotes={meetingNotes}
                            />
                        </TabPane>
                        {/* 
                        <TabPane
                            tab={t("AI Supports")}
                            key="4"
                            style={{
                                height: '100%',
                                overflow: 'auto',
                                padding: '0 16px'
                            }}
                        >
                            {aiAdvices.length > 0 ? (
                                <div>
                                    {aiAdvices.map(item => renderAdviceItem(item))}
                                </div>
                            ) : (
                                <Empty description={t("No AI Advices available")} />
                            )}
                            <ContextDrawer
                                context={selectedContext}
                                onClose={() => setSelectedContext(null)}
                            />
                        </TabPane> */}
                    </Tabs>
                </div>


                <InviteModal
                    visible={isInviteModalVisible}
                    onCancel={() => setIsInviteModalVisible(false)}
                    meetingId={id}
                    meetingTitle={meetingTitle}
                />

                <Drawer
                    title={t("Ask AI about this meeting")}
                    placement="right"
                    onClose={() => setShowAIChat(false)}
                    open={showAIChat}
                    width={600}
                >
                    <AIChatInterface
                        meetingId={id}
                        transcript={selectedTranscript}
                        onClose={() => setShowAIChat(false)}
                    />
                </Drawer>

                <ReviewReminderModal
                    visible={showReviewReminder}
                    onClose={handleReviewModalClose}
                    onReviewed={handleReviewed}
                />

                <ShareSuggestionModal
                    visible={showShareSuggestion}
                    onCancel={() => {
                        setShowShareSuggestion(false);
                        markMeetingAsVisited();
                    }}
                    meetingId={id}
                    meetingTitle={meetingTitle}
                    onShareSuccess={() => {
                        markMeetingAsVisited();
                    }}
                />

                <EndMeetingModal
                    visible={showEndMeetingModal}
                    onCancel={() => setShowEndMeetingModal(false)}
                    onConfirm={handleEndMeeting}
                    isUpdating={isUpdatingStatus}
                />
            </Content>
        </Layout >
    );
};

export default MeetingDetail;